/**
 * 业务图片面板（canvas-editor）：插入图片，依赖 trpc.assets。
 * - 上传云图片：后端鉴权 + 前端直传云存储 + 直传后后端创建 assets 记录
 * - 按名称搜索：列表按 name 模糊过滤
 * - 拖拽：有 url 的图片可拖入画布（type: media-image）；整图落在某页内时高亮该页并落入页内坐标，否则落入工作区
 */
import * as React from 'react';
import { useDraggable } from '@dnd-kit/core';
import {
  Button,
  Dropdown,
  InputGroup,
  Label,
  ProgressBar,
  Spinner,
  Tooltip,
} from '@heroui/react';
import {
  IconCloudUpload as CloudUpload,
  IconDots as Ellipsis,
  IconSearch as Search,
} from '@tabler/icons-react';
import { trpc } from '@/lib/trpc';
import {
  uploadFileDirect,
  ensureAnonymousAuth,
  getMediaTypeForMedia,
} from '@hs/shared';

const CLOUDBASE_ENV_ID = 'his-1g5d6vlz6368064f';

const MEDIA_IMAGE_TYPE = 'media-image';

function DraggableImageCard({
  id,
  url,
  name,
  children,
  className,
}: {
  id: string;
  url: string | null | undefined;
  name?: string | null;
  children: React.ReactNode;
  className?: string;
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `media-${id}`,
    data: {
      type: MEDIA_IMAGE_TYPE,
      id,
      url: url ?? '',
      name: name ?? undefined,
    },
  });

  if (!url) {
    return <div className={className}>{children}</div>;
  }

  return (
    <div
      ref={setNodeRef}
      className={className}
      {...listeners}
      {...attributes}
      style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
    >
      {children}
    </div>
  );
}

export interface ImagePanelProps {}

// 一次请求的图片数量（受后端 pageSize <= 100 限制）
const PAGE_SIZE = 10;

/** 透明格纹背景样式，便于展示透明图片 */
const checkerboardStyle: React.CSSProperties = {
  backgroundColor: '#e5e5e5',
  backgroundImage: [
    'linear-gradient(45deg, #d4d4d4 25%, transparent 5%)',
    'linear-gradient(-45deg, #d4d4d4 25%, transparent 5%)',
    'linear-gradient(45deg, transparent 75%, #d4d4d4 75%)',
    'linear-gradient(-45deg, transparent 75%, #d4d4d4 75%)',
  ].join(', '),
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
};

type UploadEntry = {
  id: string;
  file: File;
  percent: number;
  status: 'uploading' | 'done' | 'error';
  result?: { id: string; name: string; url: string };
  error?: string;
};

type MattingFeedback = {
  mediaId: string;
  status: 'loading' | 'success' | 'error';
  originalUrl: string;
  resultUrl?: string;
  resultId?: string;
  resultName?: string;
  error?: string;
};

type MediaListItem = {
  id: string | null;
  fileId: string;
  name?: string | null;
  url?: string | null;
};

function useObjectUrl(file: File | null): string | null {
  const [url, setUrl] = React.useState<string | null>(null);
  React.useEffect(() => {
    if (!file) {
      setUrl(null);
      return;
    }
    const u = URL.createObjectURL(file);
    setUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [file]);
  return url;
}

function UploadCell({ entry }: { entry: UploadEntry }) {
  const objectUrl = useObjectUrl(entry.status === 'uploading' ? entry.file : null);
  const previewUrl = entry.result?.url ?? objectUrl;
  const dragUrl = entry.status === 'done' ? entry.result?.url : null;

  const content = (
    <>
      <div className="relative w-full aspect-video overflow-hidden" style={checkerboardStyle}>
        {previewUrl ? (
          <img
            src={previewUrl}
            alt={entry.file.name}
            className="absolute inset-0 w-full h-full object-contain transform-gpu scale-110 group-hover:scale-100 transition-transform duration-300 ease-out"
          />
        ) : (
          <div className="h-full w-full flex items-center justify-center text-xs text-muted px-2 text-center">
            准备中…
          </div>
        )}
        {entry.status === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-danger/20 px-2">
            <span className="text-center text-xs text-danger">{entry.error ?? '上传失败'}</span>
          </div>
        )}
      </div>
      <div className="px-2 py-1.5 space-y-1">
        <div className="truncate text-xs text-default-foreground">{entry.file.name}</div>
        {entry.status === 'uploading' && (
          <ProgressBar value={entry.percent} minValue={0} maxValue={100} className="h-1.5">
            <ProgressBar.Track>
              <ProgressBar.Fill />
            </ProgressBar.Track>
          </ProgressBar>
        )}
        {entry.status === 'done' && (
          <div className="text-[10px] text-muted">已上传</div>
        )}
      </div>
    </>
  );

  return (
    <DraggableImageCard
      id={entry.id}
      url={dragUrl}
      name={entry.file.name}
      className="group rounded-md border border-divider overflow-hidden bg-default-100/60"
    >
      {content}
    </DraggableImageCard>
  );
}

export function ImagePanel(_props: ImagePanelProps) {
  const [uploading, setUploading] = React.useState<UploadEntry[]>([]);
  const [mattingFeedback, setMattingFeedback] = React.useState<MattingFeedback | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [page, setPage] = React.useState(1);
  const [searchKeyword, setSearchKeyword] = React.useState('');

  React.useEffect(() => {
    setPage(1);
  }, [searchKeyword]);

  const utils = trpc.useUtils();
  const authorizeUploadMutation = trpc.assets.authorizeUpload.useMutation();
  const createMediaMutation = trpc.assets.create.useMutation();
  const mattingMutation = trpc.assets.mattingAndUpload.useMutation({
    onSuccess: (result) => {
      setMattingFeedback((prev) =>
        prev
          ? {
              ...prev,
              status: 'success',
              resultUrl: (result as { url?: string | null }).url ?? '',
              resultId: result.id ?? undefined,
              resultName: result.name ?? undefined,
            }
          : null
      );
      void utils.assets.listMy.invalidate();
    },
    onError: (err) => {
      setMattingFeedback((prev) =>
        prev ? { ...prev, status: 'error', error: err.message } : null
      );
    },
  });

  const { data, isLoading } = trpc.assets.listMy.useQuery({
    page,
    pageSize: PAGE_SIZE,
    type: 'image',
    where: searchKeyword.trim()
      ? { name: { $like: `%${searchKeyword.trim()}%` } }
      : undefined,
  });

  const items = (data?.items ?? []) as MediaListItem[];
  const totalPages = data?.totalPages ?? 1;
  const hasPrev = data?.hasPrev ?? page > 1;
  const hasNext = data?.hasNext ?? page < totalPages;

  const goToPage = React.useCallback(
    (nextPage: number) => {
      if (nextPage < 1 || nextPage === page) return;
      if (data && (nextPage > (data.totalPages ?? 1))) return;
      setPage(nextPage);
    },
    [data, page]
  );

  const handleMattingClick = React.useCallback(
    (item: { id: string; url?: string | null; name?: string | null }) => {
      const url = item.url ?? '';
      setMattingFeedback({
        mediaId: item.id,
        status: 'loading',
        originalUrl: url,
      });
      mattingMutation.mutate({ id: item.id });
    },
    [mattingMutation]
  );

  const handleUploadCloudClick = React.useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleCloudFileChange = React.useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (!files?.length) return;
      const next: UploadEntry[] = [];
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith('image/')) continue;
        next.push({
          id: `upload_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 9)}`,
          file,
          percent: 0,
          status: 'uploading',
        });
      }
      if (next.length === 0) return;
      setUploading((prev) => [...prev, ...next]);
      e.target.value = '';

      const runOne = async (entry: UploadEntry) => {
        const { file } = entry;
        try {
          const { objectKey } = await authorizeUploadMutation.mutateAsync({
            fileName: file.name,
            mimeType: file.type || 'application/octet-stream',
            fileSize: file.size,
          });
          await ensureAnonymousAuth(CLOUDBASE_ENV_ID);
          const result = await uploadFileDirect(
            { envId: CLOUDBASE_ENV_ID },
            file,
            {
              path: objectKey,
              onProgress: (p) => {
                setUploading((prev) =>
                  prev.map((u) =>
                    u.id === entry.id ? { ...u, percent: p.percent } : u
                  )
                );
              },
            }
          );
          const media = await createMediaMutation.mutateAsync({
            name: file.name,
            fileId: result.fileId,
            fileSize: file.size,
            mimeType: file.type || 'application/octet-stream',
            type: getMediaTypeForMedia(file.type, file.name),
            isOwner: true,
          });
          setUploading((prev) =>
            prev.map((u) =>
              u.id === entry.id
                ? {
                    ...u,
                    percent: 100,
                    status: 'done',
                    result: {
                      id: media.id ?? '',
                      name: media.name ?? file.name,
                      url: (media as { url?: string | null }).url ?? '',
                    },
                  }
                : u
            )
          );
          void utils.assets.listMy.invalidate();
        } catch (err) {
          setUploading((prev) =>
            prev.map((u) =>
              u.id === entry.id
                ? {
                    ...u,
                    status: 'error',
                    error: err instanceof Error ? err.message : '上传失败',
                  }
                : u
            )
          );
        }
      };

      for (const entry of next) {
        void runOne(entry);
      }
    },
    [authorizeUploadMutation, createMediaMutation, utils.assets.listMy]
  );

  return (
    <div className="h-full w-full flex flex-col flex-shrink-0 rounded-lg bg-surface">
      <div className="flex-shrink-0 p-4 flex flex-col gap-2">
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          className="hidden"
          aria-hidden
          onChange={handleCloudFileChange}
        />
        <div className="flex items-center gap-2">
          <InputGroup className="min-w-0 flex-1">
            <InputGroup.Prefix>
              <Search className="size-4 text-muted" aria-hidden />
            </InputGroup.Prefix>
            <InputGroup.Input
              placeholder="按名称搜索"
              value={searchKeyword}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchKeyword(e.target.value)}
            />
          </InputGroup>
          <Tooltip>
            <Tooltip.Trigger>
              <Button
                variant="outline"
                isIconOnly
                size="sm"
                className="shrink-0"
                onPress={handleUploadCloudClick}
                aria-label="上传图片"
              >
                <CloudUpload className="h-4 w-4" />
              </Button>
            </Tooltip.Trigger>
            <Tooltip.Content placement="top" offset={6}>
              上传图片
            </Tooltip.Content>
          </Tooltip>
        </div>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto p-4">
        {mattingFeedback && (
          <div className="overflow-hidden bg-default-100/60 mb-3">
            <div
              className="aspect-square w-full max-h-40 relative"
              style={mattingFeedback.status === 'success' ? checkerboardStyle : undefined}
            >
              <img
                src={
                  mattingFeedback.status === 'success' && mattingFeedback.resultUrl
                    ? mattingFeedback.resultUrl
                    : mattingFeedback.originalUrl
                }
                alt={mattingFeedback.resultName ?? '抠图'}
                className="h-full w-full object-contain"
              />
            </div>
            <div className="px-2 py-2 flex flex-col items-center gap-1.5">
              {mattingFeedback.status === 'loading' && (
                <>
                  <Spinner size="sm" />
                  <span className="text-xs text-muted">抠图并上传云存储</span>
                </>
              )}
              {mattingFeedback.status === 'error' && (
                <span className="text-xs text-danger">{mattingFeedback.error ?? '抠图失败'}</span>
              )}
              {mattingFeedback.status === 'success' && (
                <span className="text-xs text-muted">抠图完成</span>
              )}
            </div>
          </div>
        )}
        {uploading.length > 0 && (
          <div className="grid grid-cols-2 gap-2 mb-3">
            {uploading.map((entry) => (
              <UploadCell key={entry.id} entry={entry} />
            ))}
          </div>
        )}
        {isLoading ? (
          <div className="text-sm text-muted">加载中...</div>
        ) : items.length === 0 && uploading.length === 0 ? (
          <div className="text-sm text-muted">暂无图片</div>
        ) : (
          <div className="grid grid-cols-2 gap-3">
            {items.map((item) => (
              <DraggableImageCard
                key={item.id}
                id={String(item.id)}
                url={item.url}
                name={item.name}
                className="group rounded-md border border-divider overflow-hidden bg-default-100/60"
              >
                <div title={item.name ?? '未命名图片'}>
                  <div className="relative w-full aspect-video overflow-hidden" style={checkerboardStyle}>
                    {item.url ? (
                      <img
                        src={item.url}
                        alt={item.name ?? '图片'}
                        className="absolute inset-0 w-full h-full object-contain transform-gpu scale-110 group-hover:scale-100 transition-transform duration-300 ease-out"
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-xs text-muted px-2 text-center">
                        无预览
                      </div>
                    )}
                  </div>
                  <div
                    className="px-2 py-1 flex items-center justify-between gap-1 min-h-0"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <span className="min-w-0 flex-1 truncate text-xs text-default-foreground">
                      {item.name ?? '未命名图片'}
                    </span>
                    <Dropdown>
                      <Button
                        variant="ghost"
                        isIconOnly
                        size="sm"
                        className="shrink-0"
                        aria-label="更多操作"
                      >
                        <Ellipsis className="h-4 w-4 text-muted" />
                      </Button>
                      <Dropdown.Popover placement="bottom end">
                        <Dropdown.Menu>
                          <Dropdown.Item
                            id="matting"
                            textValue="AI抠图"
                            isDisabled={!item.url || mattingMutation.isPending}
                            onAction={() =>
                              handleMattingClick({
                                id: String(item.id),
                                url: item.url,
                                name: item.name,
                              })
                            }
                          >
                            <Label>AI抠图</Label>
                          </Dropdown.Item>
                        </Dropdown.Menu>
                      </Dropdown.Popover>
                    </Dropdown>
                  </div>
                </div>
              </DraggableImageCard>
            ))}
          </div>
        )}
      </div>
      <div className="flex-shrink-0 border-t border-divider px-4 py-2 flex items-center justify-between text-xs">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" isDisabled={!hasPrev || isLoading} onPress={() => goToPage(page - 1)}>
            上一页
          </Button>
          <Button variant="ghost" size="sm" isDisabled={!hasNext || isLoading} onPress={() => goToPage(page + 1)}>
            下一页
          </Button>
        </div>
        <div className="text-muted">
          第 {data?.page ?? page} 页
          {data?.totalPages ? ` / 共 ${data.totalPages} 页` : ''}
        </div>
      </div>
    </div>
  );
}
